import React, {useEffect, useMemo, useState} from 'react';
import {setNestedObjectValues, useFormikContext} from 'formik';
import {Box, Grid, Paper, Typography, Button} from '@mui/material';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import {useSnackbar} from 'notistack';
import {LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterMoment} from '@mui/x-date-pickers-pro/AdapterMoment';
import withAutoForm, {AutoFormProps} from '../../../components/AutoForm/AutoFormNew';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {
  taskDeadlineField,
  taskNotesField,
  taskValues,
  taskContactField,
  taskSelectedEntityField,
} from './tasksFields';
import {
  AutoField,
  ButtonGroupField,
  ButtonOption,
} from '../../../components/AutoForm/AutoFieldNew';
import {Urgency} from '../../../components/Common/Urgency';
import useSaveTask, {fixSelectedEntityType} from '../useSaveTask';
import getAdjustedEndOfTaxYear from '../../../utils/getAdjustedEndOfTaxYear';
import TaskList from './TaskList';
import {useAccountContext} from '../../../contexts/AccountContext';
import UnfinishedFormPrompt from '../../../components/Common/UnfinishedFormPrompt';
import TaskEntityField from './TaskEntityField';
import Loading from '../../../components/Loading/Loading';

import {HandoverStep} from '../../../components/HandoverStep/HandoverStep';
import theme, {themeColors} from '../../../theme/Theme';
import OWNERSHIP from '../../../constants/Ownership';
import useTasks from '../useTasks';
import {
  TaskRegardingEntityEnum,
  TaskUrgencyEnum,
} from '../../../schema-types/globalTypes';
import {enumToLabel} from '../../../utils/typescriptUtils';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {UPDATES} from '../../../shared/UPDATES';
import {newUTCDate} from '../../../utils/newUTCDate';
import {AccountBasicFragment} from '../../../schema-types/AccountBasicFragment';

const classes = {
  paper: {
    padding: theme.spacing(2),
  },

  bottomContainer: {
    marginTop: theme.spacing(4),
  },

  textArea: {
    width: '100%',
    marginTop: 20,
    borderColor: theme.palette.grey[400],
  },

  buttons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'right',
  },
};

export const getContacts = (account: AccountBasicFragment | undefined) => {
  if (!account) {
    return [];
  }

  return account.contacts || [];
};

export const TasksPage: React.FC = () => {
  const {accountId, reviewId} = useParams();
  const {account, accountLoading} = useAccountContext();
  const {isFullySubmitted} = useReviewContext();

  const {closeSnackbar, enqueueSnackbar} = useSnackbar();
  const [saving, setSaving] = useState(false);
  const {values, validateForm, setFieldValue, setTouched, dirty, resetForm, errors} =
    useAutoForm<taskValues>();
  const {activityGroups, activityGroupsLoading, activityGroupsError} = useTasks();
  const {saveAndExit, saveAndContinue, saveAndExiting, saveAndContinuing} = useSaveTask();
  const [instructionsList, setInstructionsList] = useState('');
  const contactsList = useMemo(() => getContacts(account), [account]);

  useEffect(() => {
    if (values?.urgency !== TaskUrgencyEnum.TAX_YEAR_SENSITIVE) {
      return;
    }
    // set deadline to 05/04 current year or previous year
    const deadlineDate = getAdjustedEndOfTaxYear(newUTCDate());
    if (values?.deadline !== deadlineDate) {
      setFieldValue(UPDATES._1300, 'deadline', moment.utc(deadlineDate), true);
    }
  }, [values?.urgency]);

  useEffect(() => {
    // saving the contact's name for later
    setFieldValue(
      UPDATES._1301,
      'selectedEntityTypeDetails',
      contactsList?.find((x) => x.id === values?.contact)?.simplifiedName
    );
  }, [contactsList, values?.contact]);

  useEffect(() => {
    if (!activityGroups.length) {
      return;
    }
    if (values.subgroupId) {
      for (const group of activityGroups) {
        if (group.activitySubGroups) {
          for (const subgroup of group.activitySubGroups) {
            if (subgroup.id === values.subgroupId) {
              setFieldValue(UPDATES._1302, 'taskEntityOption', subgroup.requiredEntity);
              if (String(values.selectedEntityType) === 'GOAL') {
                setFieldValue(
                  UPDATES._1303,
                  'selectedEntityType',
                  fixSelectedEntityType(values.selectedEntityType)
                );
              }
              setFieldValue(
                UPDATES._1303,
                'selectedEntityType',
                subgroup.requiredEntity || TaskRegardingEntityEnum.CONTACT
              );
              setFieldValue(UPDATES._1304, 'openAccordionGroup', group.formattedName);
              setInstructionsList(subgroup.description || '');
              validateForm();
            }
          }
        }
      }
    }
  }, [values.subgroupId, activityGroups]);

  async function submitForm(exit: boolean) {
    closeSnackbar();
    setSaving(true);
    validateForm().then((validationErrors) => {
      // set all touched
      const newtouched = setNestedObjectValues({...errors}, true);
      setTouched(newtouched as any);
      setTimeout(async () => {
        if (!errors || Object.keys(errors).length === 0) {
          if (exit) {
            await saveAndExit(values, reviewId!);
          } else {
            await saveAndContinue(values, reviewId!);
          }
          setSaving(false);
        } else {
          enqueueSnackbar('Form not valid. Please check fields', {
            variant: 'error',
            autoHideDuration: null,
          });

          setSaving(false);
        }
      });
    });
  }

  const taskContactFieldMemo = useMemo<ButtonGroupField>(() => {
    // when setting a new contacts list, set the first one as the contact
    if (!values.contact) {
      setFieldValue(UPDATES._1305, 'contact', contactsList?.[0]?.id);
    }

    if (contactsList.length === 1) {
      return {
        ...taskContactField,
        options: contactsList.map<ButtonOption>((x) => ({
          label: x.simplifiedName!,
          value: x.id,
        })),
      };
    }

    // 2 contacts
    const options = [
      ...contactsList.map<ButtonOption>((x) => ({
        label: x.simplifiedName!,
        value: x.id,
      })),
      {label: OWNERSHIP.JOINT_LIVES, value: OWNERSHIP.JOINT_CODE},
    ];
    return {
      ...taskContactField,
      options,
    };
  }, [contactsList]);

  const taskEntityOptions: ButtonOption<TaskRegardingEntityEnum>[] = useMemo(() => {
    const options: ButtonOption<TaskRegardingEntityEnum>[] = [];
    if (
      values.taskEntityOption &&
      values.taskEntityOption !== TaskRegardingEntityEnum.CONTACT
    ) {
      options.push({
        label: enumToLabel(values.taskEntityOption),
        value: values.taskEntityOption,
      });
      options.push({
        label: 'Contact',
        value: TaskRegardingEntityEnum.CONTACT,
        disabled: Boolean(account?.isTrust),
      });
    }
    return options;
  }, [values.taskEntityOption, account?.isTrust]);

  const selectedEntityFieldMemo = useMemo(() => {
    return {...taskSelectedEntityField, options: taskEntityOptions};
  }, [taskEntityOptions]);

  if (!account) {
    return <Loading />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <HandoverStep />
      <Box mt={2} />
      <Paper sx={classes.paper}>
        <Box mt={1} mb={2} ml={1} mr={1}>
          <Grid alignItems="center" container spacing={4}>
            <Grid item xs={4}>
              <Typography variant="h5" style={{fontWeight: 'bold'}}>
                Create task
              </Typography>
              <Box mt={2} />
              <AutoField field={taskContactFieldMemo} />
            </Grid>
            <Grid item xs={4}>
              <Urgency direction="row" />
            </Grid>
            <Grid item xs={2}>
              <AutoField
                key={`${values.deadline}`}
                field={{
                  ...taskDeadlineField,
                  initialValue: values.deadline
                    ? moment.utc(values.deadline).toISOString() ?? undefined
                    : undefined,
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Box mt={4} />
      <Paper sx={classes.paper}>
        <Box mt={1} mb={2} ml={1} mr={1}>
          <Grid container alignContent="flex-start" spacing={4}>
            <Grid item xs={4}>
              <Typography
                style={{
                  marginBottom: 10,
                  color: errors.subgroupId ? themeColors.red : 'inherit',
                }}
                variant="h6"
              >
                Select task (required):
              </Typography>

              <TaskList
                activityGroups={[...(activityGroups || [])].sort((a, b) => {
                  return a.formattedName?.localeCompare(b.formattedName || '') || 0;
                })}
                setActivityGroup={(x) =>
                  setFieldValue(UPDATES._1304, 'openAccordionGroup', x)
                }
                activityGroup={values.openAccordionGroup!}
                setInstructionsList={setInstructionsList}
                setTaskEntityOption={(x) => {
                  setFieldValue(UPDATES._1302, 'taskEntityOption', x);
                }}
                subgroupId={values.subgroupId}
                activityGroupsLoading={activityGroupsLoading}
                activityGroupsError={activityGroupsError}
              />
            </Grid>
            <Grid item xs={8}>
              {taskEntityOptions.length > 1 ? (
                <>
                  <AutoField field={selectedEntityFieldMemo} />
                  <Box mt={4} />
                  {values.selectedEntityType !== TaskRegardingEntityEnum.CONTACT && (
                    <Box mt={1} mb={4}>
                      <TaskEntityField
                        accountLoading={accountLoading}
                        contactsList={contactsList}
                      />
                    </Box>
                  )}
                </>
              ) : null}
              <Grid container alignContent="flex-start" spacing={4}>
                <Grid item xs={6}>
                  {instructionsList ? (
                    <>
                      <Typography variant="h6">Instructions:</Typography>

                      <Typography variant="body1" style={{whiteSpace: 'pre-line'}}>
                        {instructionsList}
                      </Typography>
                    </>
                  ) : null}
                </Grid>
                {values.subgroupId ? (
                  <Grid item xs={6}>
                    <Box mt={1} mb={4}>
                      <AutoField field={taskNotesField} />
                    </Box>
                  </Grid>
                ) : null}
              </Grid>
            </Grid>

            <Grid container style={{paddingBottom: 10}} spacing={2}>
              <Grid item xs={6} />
              <Grid item xs={6} sx={classes.buttons}>
                <UnfinishedFormPrompt
                  path={`/account/${accountId}/review-journey/${reviewId}/tasks`}
                  dirty={dirty}
                />
                <Box ml={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={saving || saveAndExiting || isFullySubmitted}
                    onClick={() => {
                      submitForm(true);
                    }}
                  >
                    Save and exit
                  </Button>
                </Box>
                <Box ml={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={saving || saveAndContinuing || isFullySubmitted}
                    onClick={() => {
                      submitForm(false);
                    }}
                  >
                    Save and add another task
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </LocalizationProvider>
  );
};

export const TasksForm = withAutoForm<taskValues, AutoFormProps<taskValues>>(TasksPage);
